import { Col, Divider, Form, Input, Result, Row, Select, Spin, Table, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { FormFooter } from '../Shared/FormFooter';
import { FormContainer, FormItemStyled, PageTitle, PrimaryButton } from '../Styled/GlobalStyles';
import { CommonForm } from '../Shared/CommonForm';
import TextArea from 'antd/es/input/TextArea';
import { ErrorResponseDto } from '../../models/ErrorResponseDto';
import { showNotification } from '../../util/Notifications';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { logEvent } from '../../util/Analytics';
import { logUserTrack } from '../Shared/userTrack';

type railLocationOption = {
  code: string;
  address: string;
};

const RailRequest = () => {
  const [form] = Form.useForm();
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(true);
  const [railLocationOptions, setRailLocationOptions] = useState<railLocationOption[]>([]);
  const [railLocation, setRailLocation] = useState<any>(null);
  const [railLocationError, setRailLocationError] = useState<ErrorResponseDto | null>(null);
  const [isRailLocationLoading, setIsRailLocationLoading] = useState<boolean>(false);

  const billOfLadingValue = Form.useWatch('billOfLading', form);
  const railLocationValue = Form.useWatch('railLocation', form);
  const passwordValue = Form.useWatch('password', form);

  useEffect(() => {
    getRailLocationDropdownOptions();
  }, []);

  useEffect(() => {
    if (!billOfLadingValue || !railLocationValue || !passwordValue) {
      setIsFormDisabled(true);
      return;
    } else if (billOfLadingValue.length >= 5) {
      setIsFormDisabled(false);
      return;
    }
    setIsFormDisabled(true);
  }, [billOfLadingValue, railLocationValue, passwordValue]);

  const getRailLocationDropdownOptions = () => {
    fetch(`/api/railRequest/options`, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setRailLocationOptions(data);
      });
  };

  const onFormSubmit = (values: any) => {
    logEvent('rail_request_submit');

    if (!values.billOfLading && !values.releaseDate && !values.password) {
      return;
    }

    try {
      logUserTrack('click', 'submit_rail_request_form', values);
    } catch (error) {
      console.log(error);
    }

    setIsRailLocationLoading(true);
    fetch(`/api/railRequest`, {
      method: 'post',
      body: JSON.stringify(values),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.status) {
          setRailLocation(data);
        } else {
          setRailLocationError(data);
          showNotification({
            type: 'error',
            title: 'Error',
            message: data.message,
          });
        }
      })
      .finally(() => setIsRailLocationLoading(false));
  };

  const resetForm = () => {
    form.resetFields();
    setRailLocationError(null);
    setRailLocation(null);
  };

  const userClickedClearForm = () => {
    logEvent('rail_request_clear');
    resetForm();
  };

  const userClickedSearchAgain = () => {
    logEvent('rail_request_search_again');
    resetForm();
  };

  const userClickedGoBack = () => {
    logEvent('rail_request_search_again');
    resetForm();
  };

  useEffect(() => {
    document.title = 'Rail Move Request';
    // logPageView('Rail Move Request');
  }, []);

  const successResponseTableColumns = [
    {
      title: 'Bill Of Lading',
      dataIndex: 'billOfLading',
    },
    {
      title: 'Container Number',
      dataIndex: 'containerNumber',
    },
  ];

  return (
    <>
      <PageTitle>Rail Move Request</PageTitle>
      {railLocationError && (
        <FormContainer>
          <Result
            status='error'
            title='Error'
            subTitle={railLocationError.message}
            icon={<QuestionCircleOutlined />}
            extra={[
              <PrimaryButton type='primary' key='console' onClick={userClickedSearchAgain}>
                Search Again
              </PrimaryButton>,
            ]}
          ></Result>
        </FormContainer>
      )}
      {railLocation && (
        <>
          <FormContainer>
            <Result
              status='success'
              title='Rail Request Created'
              subTitle={
                <>
                  <Typography.Text>Rail Request Created For {railLocation.railLocation}</Typography.Text>
                  <Row justify={'center'} style={{ marginTop: 20 }}>
                    <Col span={12}>
                      <Table
                        bordered={true}
                        columns={successResponseTableColumns}
                        dataSource={railLocation.containers}
                        pagination={false}
                      />
                    </Col>
                  </Row>
                </>
              }
              extra={[
                <PrimaryButton type='primary' key='console' onClick={userClickedGoBack}>
                  Go Back
                </PrimaryButton>,
              ]}
            />
          </FormContainer>
        </>
      )}
      {!railLocation && !railLocationError && (
        <CommonForm form={form} onFinish={onFormSubmit}>
          <Spin spinning={isRailLocationLoading} size='large'>
            <FormContainer>
              <FormItemStyled name='billOfLading' label='BOL'>
                <Input placeholder='Enter BOL' size='large' />
              </FormItemStyled>
              <FormItemStyled name='railLocation' label='Rail Location'>
                <Select placeholder='Select Rail Location' size='large'>
                  {railLocationOptions.map((option) => (
                    <React.Fragment key={option.code}>
                      <Select.Option key={option.code} value={option.code}>
                        <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 0 }}>
                          {option.code}
                        </Typography.Title>
                        <Typography.Text>{option.address}</Typography.Text>
                        <Divider style={{ margin: 5 }} />
                      </Select.Option>
                    </React.Fragment>
                  ))}
                </Select>
              </FormItemStyled>

              <FormItemStyled name='instructions' label='Instructions'>
                <TextArea placeholder='Enter Instructions' size='large' />
              </FormItemStyled>
              <FormItemStyled name='password' label='Password'>
                <Input type='password' size='large' />
              </FormItemStyled>
            </FormContainer>
            <FormFooter searchDisabled={isFormDisabled} submitText={'Submit'} resetForm={userClickedClearForm} />
          </Spin>
        </CommonForm>
      )}
    </>
  );
};

export default RailRequest;
